import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { navLinks } from "@/lib/weddingData";

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(`/${link.id}`);
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    } else {
      document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-[#c9c3b3] bg-[#ECE7DC]/95 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1800px] items-center justify-between px-6 py-6 md:px-10">
          <button
            onClick={goHome}
            data-testid="nav-monogram"
            className="flex items-center leading-none"
            aria-label="Ir al inicio"
          >
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_9e44ceb2-a107-446e-9b9e-3beb4b654377/artifacts/x4cus0vq_mvlogo.png"
              alt="M & V"
              className="h-10 w-auto md:h-12"
              draggable={false}
            />
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            aria-label="Abrir menú"
            className="flex items-center gap-3 font-mono text-sm uppercase tracking-[0.12em]"
          >
            <span>Menú</span>
            <Menu strokeWidth={1.5} className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Full-screen menu — red panel with beige typography */}
      <div
        data-testid="nav-mobile-sheet"
        aria-hidden={!open}
        className={`fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-[#891613] text-[#ECE7DC] transition-opacity duration-500 ${
          open ? "pointer-events-auto opacity-100 visible" : "pointer-events-none opacity-0 invisible"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-6 md:px-10">
          <button onClick={goHome} className="flex items-center leading-none" aria-label="Ir al inicio">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_9e44ceb2-a107-446e-9b9e-3beb4b654377/artifacts/x4cus0vq_mvlogo.png"
              alt="M & V"
              className="h-10 w-auto md:h-12"
              draggable={false}
              style={{ filter: "brightness(0) invert(93%) sepia(11%) saturate(287%) hue-rotate(3deg) brightness(94%) contrast(90%)" }}
            />
          </button>
          <button
            data-testid="nav-mobile-close"
            onClick={() => setOpen(false)}
            aria-label="Cerrar menú"
            className="flex items-center gap-3 font-mono text-sm uppercase tracking-[0.12em]"
          >
            <span>Cerrar</span>
            <X strokeWidth={1.5} className="h-5 w-5" />
          </button>
        </div>

        <nav className="mx-auto mt-12 flex max-w-[1800px] flex-col gap-4 px-6 md:mt-24 md:px-10">
          {navLinks.map((l, idx) => (
            <button
              key={l.id}
              onClick={() => go(l)}
              data-testid={`nav-link-${l.id}`}
              className="group flex items-baseline justify-between border-b border-[#ECE7DC]/25 pb-4 text-left"
            >
              <span
                className="display-xl transition-transform duration-500 group-hover:translate-x-2"
                style={{ color: "#ECE7DC" }}
              >
                {l.label}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#ECE7DC]/70">
                0{idx + 1}
              </span>
            </button>
          ))}
        </nav>

        <div className="mx-auto mt-16 flex max-w-[1800px] flex-wrap items-end justify-between gap-8 px-6 pb-10 md:px-10">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#ECE7DC]/70">Fecha</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.24em]">04 · 06 · 2027</p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#ECE7DC]/70">Lugar</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.24em]">Cortijo de Enmedio, Granada</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
